import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { AlertCircle, StickyNote, Target } from 'lucide-react';
import { useFarmingResultsSelection } from '../../context/FarmingResultsSelectionContext';
import { fetchFarmingNotes } from '../../services/farmingNotesService';
import { Timestamp } from 'firebase/firestore';

interface NoteEntry {
  date: Timestamp;
  note: string;
  source: 'Standard ASIN Review' | 'Client Note';
  daysAgo: number;
}

export const NotesDashboard: React.FC = () => {
  const { selectedCompany, selectedProduct } = useFarmingResultsSelection();
  const [notes, setNotes] = useState<NoteEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selectedCompany && selectedProduct) {
      fetchData();
    }
  }, [selectedCompany, selectedProduct]);

  const fetchData = async () => {
    if (!selectedCompany || !selectedProduct) return;

    const asin = selectedProduct.id || selectedProduct.ASIN;
    if (!asin) {
      setError('No ASIN found for selected product');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`Fetching notes for: ${selectedCompany}/${asin}`);
      const result = await fetchFarmingNotes(selectedCompany, asin);
      setNotes(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch notes';
      console.error('Error fetching notes:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!selectedCompany || !selectedProduct) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <Target className="h-12 w-12 text-app-primary mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Select Company & ASIN
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Choose a company and ASIN from the sidebar to view notes.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-app-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading notes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Error Loading Data
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {error}
          </p>
          <Button onClick={fetchData} variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (notes.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <StickyNote className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No Notes Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            No notes from the last 35 days for this ASIN.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <StickyNote className="h-5 w-5 text-app-primary" />
            Notes (Last 35 Days)
          </CardTitle>
          <CardDescription>
            Combined notes from Standard ASIN Review and Client Note for {selectedCompany} - {selectedProduct.id}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="w-full">
            <div className="w-full">
              <div className="flex border-b dark:border-gray-700 py-4 px-4 font-semibold text-gray-900 dark:text-gray-100 text-sm">
                <div className="w-32 text-center">Days Ago</div>
                <div className="flex-1 text-left pl-4">Note</div>
              </div>
              
              <div className="space-y-0">
                {notes.map((note, index) => (
                  <div key={`${note.daysAgo}-${index}`} className="flex border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 py-4 px-4 text-sm">
                    <div className="w-32 text-center text-gray-900 dark:text-gray-100">
                      {note.daysAgo}
                    </div>
                    <div className="flex-1 text-left text-gray-900 dark:text-gray-100 pl-4">
                      {note.note}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotesDashboard;